import {Component, EventEmitter, Injector, Input, OnChanges, OnInit, Output} from '@angular/core';
import {MatDialog, MatDialogConfig} from "@angular/material/dialog";
import {BasePageComponent} from "@core-components/page-content/base-page.component";
import {SelectableCategory} from "@features/settings/industries/industry.model";
import {ManageIndustriesService} from "@features/settings/industries/manage-industries.service";
import {ConfirmationDialogComponent} from "@shared-components/confirmation-dialog/confirmation-dialog.component";
import {LoadingService} from "@ui-kit/framework/util/service/loading.service";
import {forkJoin, lastValueFrom} from "rxjs";
import {CrudSpaces} from "../model/crud-spaces.model";
import {OfferingSpacesInterface} from "../model/offering-spaces.interface";
import {ManageSpacesService} from "../service/manage-spaces.service";

@Component({
  selector: 'app-list-spaces',
  templateUrl: './list-spaces.component.html',
  styleUrls: ['./list-spaces.component.scss']
})
export class ListSpacesComponent extends BasePageComponent implements OnInit, OnChanges {
  @Input("dirty") selectedSpaceDirty!: boolean;
  @Input() spaceUpdated!: OfferingSpacesInterface | null;
  @Output() spaceSelected = new EventEmitter<OfferingSpacesInterface>();
  @Output() addNewSpace = new EventEmitter();

  allIndustries: SelectableCategory[] = [];

  spacesData: CrudSpaces[] = [];
  updatedRow: CrudSpaces | null = null; // Only set when data is actually updated from edit form

  protected readonly CrudSpaces = CrudSpaces;

  constructor(
    injector: Injector,
    private spaceService: ManageSpacesService,
    public loader: LoadingService,
    private matDialog: MatDialog,
    private industriesService: ManageIndustriesService
  ) {
    super(injector);
  }

  ngOnInit(): void {
    this.loadIndustries();
  }

  loadIndustries() {
    lastValueFrom(this.industriesService.getAllSelectableOptionsForOffering())
      .then((res: SelectableCategory[]) => {
        this.allIndustries = res;
        this.loadSpaces();
      });
  }

  ngOnChanges(changes: any) {
    // This is like handleFormSubmission in master-detail - when parent tells us data was updated
    if (changes.spaceUpdated && changes.spaceUpdated.currentValue && !changes.spaceUpdated.firstChange && this.allIndustries) {
      const updatedSpace = changes.spaceUpdated.currentValue as OfferingSpacesInterface;
      const index = this.spacesData.findIndex(space => space.id === updatedSpace.id);

      const crudSpace = new CrudSpaces(updatedSpace, this.allIndustries);
      if (index !== -1) {
        this.spacesData[index] = crudSpace;
      } else {
        this.spacesData.push(crudSpace);
      }

      // Update table data
      this.spacesData = [...this.spacesData];

      // Set updatedRow to tell CrudTable to highlight this updated row
      this.updatedRow = crudSpace;
    }
  }

  loadSpaces() {
    const loadUnavailableSpaces$ = this.spaceService.getSpacesForOffering({spaceName: null, locked: true});
    const loadAvailableSpaces$ = this.spaceService.getSpacesForOffering({spaceName: null, locked: false});

    forkJoin([loadUnavailableSpaces$, loadAvailableSpaces$]).subscribe(([unavailableSpaces, availableSpaces]) => {
      const allSpaces = [...unavailableSpaces, ...availableSpaces];
      this.spacesData = allSpaces.map(space => new CrudSpaces(space, this.allIndustries));
    });
  }

  // This is like rowSelected in master-detail - simple and clean
  rowSelected($event: any): void {
    if ($event) {
      const selectedSpace = ($event as CrudSpaces).toOfferingSpacesInterface();
      this.handleSpaceSelection(selectedSpace);
    }
  }

  private handleSpaceSelection(space: OfferingSpacesInterface) {
    if (this.selectedSpaceDirty) {
      this.showConfirmationDialog(space);
      return;
    }

    // Just emit to parent - let parent handle the selection state
    this.spaceSelected.emit(space);
  }

  private showConfirmationDialog(space: OfferingSpacesInterface) {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.data = {
      message: 'There are unsaved changes to the space you are currently editing. Are you sure you want to change the space?',
      buttonConfirmLabel: 'Discard changes',
      buttonCancelLabel: 'Cancel'
    };

    const dialogRef = this.matDialog.open(ConfirmationDialogComponent, dialogConfig);
    dialogRef.afterClosed().subscribe(result => {
      if (result === true) {
        this.spaceSelected.emit(space);
      }
    });
  }

  addNewSpaceEvent() {
    this.addNewSpace.emit({instruction: 'addNewSpace'})
  }
}
